import { gameSystems } from "@/data/globals";
import { GameSystems, IGameRule, IGameSystem, ISpecialRule } from "@/data/types";
import { questifyRuleText } from "@/services/rules/questifyHelper";
import skirmifyRulesText from "@/services/rules/skirmificationHelper";
import { groupBy, sumBy } from "lodash-es";
import { GameSystemService } from "../GameSystemService";
import { halfNumberRange } from "../Helpers";

interface IGroupedSpecialRule extends ISpecialRule {
  count?: number;
}

export default class RulesService {
  public static group(rules: ISpecialRule[], forceStack: boolean = false): IGroupedSpecialRule[] {
    const groups = groupBy(rules, (rule) => rule.name);
    return Object.keys(groups).map((key) => {
      const group = groups[key];
      const rule = group[0];
      const hasRating = Boolean(rule.rating);
      const stack = hasRating && (forceStack || rule.name === "Tough");
      const isStringRating = hasRating && isNaN(parseInt(rule.rating as string));
      const rating = (() => {
        if (!hasRating || isStringRating) return null;
        const ratings = group.map((rule) => parseInt(rule.rating as any));
        if (stack) {
          if (rule.name === "Armor") return Math.min(...ratings);
          return group.reduce(
            (total, next) => (next.rating ? total + parseInt(next.rating as any) : total),
            0
          );
        }

        return Math.max(...ratings);
      })();
      return {
        ...rule,
        count: stack ? 0 : sumBy(group, (x: any) => x.count || 1),
        rating: isStringRating ? rule.rating : rating?.toString()
      };
    });
  }

  public static displayName(
    rule: { name: string; rating?: any; modify?: boolean },
    count: number = null,
    hideRatingValue: boolean = false
  ) {
    if (typeof rule === "string") return rule as string;

    const countStr = count > 1 ? `${count}x ` : "";
    const ratingStr = rule.rating
      ? `(${(rule.modify ? "+" : "") + rule.rating + (rule.name === "Armor" ? "+" : "")})`
      : "";
    return countStr + rule.name + (hideRatingValue && ratingStr ? "(X)" : ratingStr);
  }

  public static enrichAlias(alias, parent) {
    return {
      ...parent,
      id: alias.id,
      aliasedRuleId: alias.aliasedRuleId,
      name: alias.name,
      originalName: parent.name,
      // Aliases can never be base or common themselves
      isBase: false,
      isCommon: false
      // TODO: Which army books are using an alias?
      //armyBooks: [{ uid: x.armyBookUid, name: x.armyName }] // remove armyBooks from parent
    };
  }

  public static applyDescriptionOverride<T extends { description: string; descriptionOverride?: any }>(
    input: T,
    gameSystem: IGameSystem,
    calcSettings: any
  ) {
    const { descriptionOverride, ...rule } = input;

    try {
      const isSkirmish = GameSystemService.isSkirmish(gameSystem);
      const isRegiments = gameSystems.aofr.id === gameSystem.id;

      const applySkirmish = () => {
        if (descriptionOverride?.["skirmish"]) {
          rule.description = descriptionOverride["skirmish"];
        } else {
          // We auto skirmify if no override is found
          rule.description = skirmifyRulesText(rule.description, calcSettings);
        }
      };

      if (isRegiments && descriptionOverride?.["regiments"]) {
        rule.description = descriptionOverride["regiments"];
      } else if (isSkirmish) {
        applySkirmish();
      }
      // Player hero quest
      else if ([GameSystems.aofq, GameSystems.gfsq].includes(gameSystem.id)) {
        if (descriptionOverride?.["quest"]) {
          rule.description = descriptionOverride["quest"];
        } else {
          applySkirmish();
          rule.description = questifyRuleText(rule.description, false, calcSettings);
        }
      }
      // Quest AI
      else if ([GameSystems.aofqai, GameSystems.gfsqai].includes(gameSystem.id)) {
        if (descriptionOverride?.["quest ai"]) {
          rule.description = descriptionOverride["quest ai"];
        } else {
          applySkirmish();
          rule.description = questifyRuleText(rule.description, true, calcSettings);
        }
      }

      return rule;
    } catch (error) {
      console.error(`Could not apply description override for ${gameSystem?.slug} -> ${error.message}`, rule);
      return rule;
    }
  }

  public static findReferencedRules(
    rule: { name: string; description: string },
    ruleDefinitions: { name: string; description: string }[]
  ) {
    const usedRuleDefs: { name: string; description: string }[] = [];
    for (let match of ruleDefinitions) {
      // Don't match against self
      if (match.name === rule.name) continue;

      const escapedName = match.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

      // TODO: This should be data driven based on rule "Has Rating"?
      if (escapedName === "Defense") {
        if (new RegExp("Defense\\(").test(rule.description)) {
          usedRuleDefs.push(match);
        }
      } else if (new RegExp(escapedName + "[\\s\\.\\(]").test(rule.description)) {
        usedRuleDefs.push(match);
      }
    }

    for (let i = usedRuleDefs.length - 1; i >= 0; i--) {
      const rule = usedRuleDefs[i];
      let remove = false;
      for (const otherRule of usedRuleDefs) {
        if (rule.name === otherRule.name) continue;
        remove ||= otherRule.name.includes(rule.name);
      }

      if (remove) {
        usedRuleDefs.splice(i, 1);
      }
    }
    return usedRuleDefs;
  }

  public static hasCaster(specialRules: { name: string }[], ruleDefinitions: IGameRule[]) {
    let result = false;
    for (let r of specialRules ?? []) {
      result ||= r.name === "Caster";
      const ruleDef = ruleDefinitions.find((rd) => rd.name === r.name);
      const ruleDesc = ruleDef?.description;
      result ||= ruleDesc && /hav.+Caster\([X\d]\)/i.test(ruleDesc);
    }
    return result;
  }

  public static applyHalfRange(description: string) {
    if (!description) return description;
    const randomDistanceTest = /(D\d\+\d+["”“])/;
    const randomDistanceMatch = description.match(randomDistanceTest);
    if (randomDistanceMatch) {
      const randomDistance = randomDistanceMatch[1];
      return description.replace(randomDistanceTest, `half of ${randomDistance}`);
    }
    return description.replace(/(?<!rolls of )(\d+["”“])/gi, (match, p1) => {
      return `${halfNumberRange(p1)}"`;
    });
  }
}
